import React from "react";
import { Link } from "gatsby";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { teamMembers } from "@/data/teamMembers";
import { ArrowRight } from "lucide-react";

interface TeamPreviewProps {
  title?: string;
  subtitle?: string;
  ctaLabel?: string;
  ctaHref?: string;
  className?: string;
}

const TeamPreview = ({
  title = "Meet Our Dentists",
  subtitle = "Get to know the dentists who provide calm, gentle, and modern care for families across Melbourne's northern suburbs.",
  ctaLabel = "Meet Our Team",
  ctaHref = "/about/our-team",
  className = "bg-background",
}: TeamPreviewProps) => {
  return (
    <section className={`py-24 ${className}`}>
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-4">
            {title}
          </h2>
          <p className="text-lg text-neutral-800">
            {subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {teamMembers.map((member) => (
            <Card key={member.name} className="border-0 shadow-soft hover:shadow-medium transition-gentle">
              <CardContent className="p-6 text-center">
                <div className="w-28 h-28 mx-auto mb-4 rounded-full overflow-hidden border-4 border-white shadow-soft">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top scale-110"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <h3 className="text-xl font-semibold text-neutral-800 mb-1">{member.name}</h3>
                <p className="text-sm text-primary font-medium mb-3">{member.role}</p>
                <p className="text-sm text-neutral-700 leading-relaxed">{member.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-10">
          <Button variant="outline" size="xl" asChild>
            <Link to={ctaHref}>
              {ctaLabel}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default TeamPreview;
